/**
 * 封面图片上传组件 - 使用 Cloudflare Images
 *
 * 第一性原理分析：
 * 1. 核心流程：图片选择 → Base64编码 → 上传到Cloudflare → 返回URL
 * 2. 关键优势：Cloudflare Images自动优化（WebP转换、响应式variants）
 * 3. 成本控制：免费层100,000张图片/月，超出$5/10万张
 */

import React, { useState, useEffect } from 'react';
import { Upload, Card, Image, Space, Typography, Button, App } from 'antd';
import { PictureOutlined, CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { supabaseClient } from '../../utility/supabaseClient';

const { Text } = Typography;

interface PosterUploadProps {
  movieId?: string;
  usageType?: 'list' | 'detail' | 'hero';
  value?: string; // imageUrl
  onChange?: (imageUrl: string) => void;
  maxSizeMB?: number;
}

const RATIO_TOLERANCE = 0.06; // 6% 允许误差，避免过度苛刻
const RATIO_MAP: Record<NonNullable<PosterUploadProps["usageType"]>, number> = {
  list: 16 / 9,
  hero: 16 / 9,
  detail: 9 / 16,
};

export const PosterUpload: React.FC<PosterUploadProps> = ({
  movieId,
  usageType = 'list',
  value,
  onChange,
  maxSizeMB = 10,
}) => {
  const { message } = App.useApp(); // 使用App的message，支持动态主题
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | undefined>(value);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadError, setUploadError] = useState<string | undefined>();

  // 当外部value变化时同步更新
  useEffect(() => {
    setImageUrl(value);
    if (value) {
      setFileList([{
        uid: '-1',
        name: 'poster.jpg',
        status: 'done',
        url: value,
      }]);
    }
  }, [value]);

  /**
   * 第一性原理：图片上传流程
   *
   * [用户选择图片]
   *   ↓ (因为：需要验证格式和大小)
   * [客户端验证]
   *   ↓ (因为：Cloudflare Images API接受base64)
   * [转换为Base64]
   *   ↓ (因为：需要管理员权限)
   * [调用Edge Function上传]
   *   ↓ (关键：Cloudflare自动生成多种尺寸variants)
   * [返回optimized image URL]
   */
  const handleUpload: UploadProps['beforeUpload'] = async (file) => {
    // 1. 文件验证
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return Upload.LIST_IGNORE;
    }

    const isLtMaxSize = file.size / 1024 / 1024 < maxSizeMB;
    if (!isLtMaxSize) {
      message.error(`图片大小不能超过 ${maxSizeMB}MB！`);
      return Upload.LIST_IGNORE;
    }

    // 1.1 比例校验
    const targetRatio = RATIO_MAP[usageType];
    if (targetRatio) {
      try {
        const { width, height } = await getImageDimensions(file);
        const ratio = width / height;
        const ratioDiff = Math.abs(ratio - targetRatio) / targetRatio;
        if (ratioDiff > RATIO_TOLERANCE) {
          message.error(
            usageType === 'detail'
              ? '请上传9:16竖版封面（推荐1080x1920）'
              : '请上传16:9横版封面（推荐1920x1080）'
          );
          return Upload.LIST_IGNORE;
        }
      } catch (e) {
        message.error('无法读取图片尺寸，请重试');
        return Upload.LIST_IGNORE;
      }
    }

    setUploading(true);
    setUploadError(undefined);

    try {
      // 2. 转换为Base64
      const base64 = await getBase64(file);

      // 3. 获取认证token
      const { data: authData, error: authError } = await supabaseClient.auth.getSession();
      if (authError || !authData.session) {
        throw new Error('请先登录');
      }

      // 4. 调用Edge Function上传
      const uploadEndpoint = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/upload-custom-poster`;

      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authData.session.access_token}`,
        },
        body: JSON.stringify({
          movieId: movieId,
          usageType: usageType,
          file: {
            name: file.name,
            type: file.type,
            data: base64,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '上传失败');
      }

      const { imageUrl: uploadedUrl, imageId } = await response.json();

      // 5. 更新状态
      setImageUrl(uploadedUrl);
      message.success('封面上传成功！');

      // 通知父组件
      if (onChange) {
        onChange(uploadedUrl);
      }

      // 更新文件列表
      setFileList([
        {
          uid: imageId || '-1',
          name: file.name,
          status: 'done',
          url: uploadedUrl,
        },
      ]);

    } catch (error: any) {
      console.error('Upload failed:', error);
      setUploadError(error.message);
      message.error(error.message || '上传失败');
    } finally {
      setUploading(false);
    }

    return false; // 阻止默认上传行为
  };

  // 辅助函数：将文件转换为Base64
  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // 读取图片尺寸，用于比例校验
  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img: HTMLImageElement = new window.Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = (err: string | Event) => reject(err);
        img.src = reader.result as string;
      };
      reader.onerror = (err: ProgressEvent<FileReader>) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  // 删除图片
  const handleRemove = () => {
    setImageUrl(undefined);
    setFileList([]);
    setUploadError(undefined);
    if (onChange) {
      onChange('');
    }
  };

  return (
    <div>
      {!imageUrl ? (
        <Upload.Dragger
          name="file"
          multiple={false}
          beforeUpload={handleUpload}
          fileList={fileList}
          showUploadList={false}
          accept="image/*"
          disabled={uploading}
        >
          <p className="ant-upload-drag-icon">
            <PictureOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          </p>
          <p className="ant-upload-text">
            {uploading ? '正在上传...' : '点击或拖拽图片到此区域上传'}
          </p>
          <p className="ant-upload-hint">
            支持格式：JPG, PNG, WebP, GIF | 最大：{maxSizeMB}MB
          </p>
        </Upload.Dragger>
      ) : (
        <Card
          size="small"
          style={{ marginTop: 16 }}
          cover={
            <div style={{ padding: 12, background: '#f5f5f5' }}>
              <Image
                src={imageUrl}
                alt="封面预览"
                style={{
                  width: '100%',
                  aspectRatio:
                    usageType === 'detail'
                      ? '9 / 16'
                      : usageType === 'hero' || usageType === 'list'
                      ? '16 / 9'
                      : undefined,
                  height: '100%',
                  minHeight: 240,
                  objectFit: 'cover',
                  borderRadius: 10,
                }}
              />
            </div>
          }
          actions={[
            <Button
              key="delete"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
            >
              删除
            </Button>,
          ]}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircleOutlined style={{ fontSize: 16, color: '#52c41a' }} />
              <Text strong style={{ color: '#52c41a' }}>封面已上传</Text>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              URL: <Text copyable={{ text: imageUrl }}>{imageUrl.substring(0, 50)}...</Text>
            </Text>
          </Space>
        </Card>
      )}

      {/* 错误提示 */}
      {uploadError && (
        <Card size="small" style={{ marginTop: 16, borderColor: '#ff4d4f' }}>
          <Space>
            <CloseCircleOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />
            <div>
              <Text strong style={{ color: '#ff4d4f' }}>上传失败</Text>
              <br />
              <Text type="secondary">{uploadError}</Text>
            </div>
          </Space>
        </Card>
      )}

      {/* 使用说明 */}
      {!imageUrl && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          提示：上传后图片将自动优化为 WebP 格式，并生成多种尺寸以适配不同设备
        </Text>
      )}
    </div>
  );
};
